import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocument } from "@/office/word/document";
import { anzahlFormeln, readBodyOoxml } from "@/office/word/einfuegen";

const PFEILE = /[→←↑↓↔⇒⇐⇔➔]/u;

export const lektionSymboleFormeln: Lesson = {
  id: "symbole-formeln",
  title: "Symbole, Sonderzeichen und Formeln",
  summary: "Zeichen, die nicht auf der Tastatur stehen – und Formeln, die mehr sind als Text.",
  durationMinutes: 9,
  learningGoals: [
    "Symbole über Einfügen → Symbol und über AutoKorrektur-Kürzel einfügen",
    "Geschützte Leerzeichen und bedingte Trennstriche kennen",
    "Eine Formel mit dem Formel-Editor setzen",
  ],
  steps: [
    {
      id: "symbol",
      title: "Symbole einfügen",
      explanation: [
        "„Einfügen“ → „Symbol“ zeigt die zuletzt verwendeten Zeichen; „Weitere Symbole …“ " +
          "öffnet die vollständige Zeichentabelle. Im Feld „Subset“ lässt sich gezielt zu " +
          "Pfeilen, Währungen oder mathematischen Operatoren springen.",
        "Viele Zeichen kommen schneller über die AutoKorrektur: (c) wird zu ©, -> zu →, " +
          "=> zu ⇒. Die Registerkarte „Sonderzeichen“ im Symbol-Dialog listet außerdem die " +
          "Tastenkürzel für Gedankenstrich, Paragraph und Co.",
        "Wichtig ist, echte Zeichen statt Ersatzkonstruktionen zu verwenden: Ein Pfeil aus " +
          "Bindestrich und Größer-Zeichen wird vorgelesen als „Minus größer als“.",
      ],
      keyPoints: [
        "Einfügen → Symbol → Weitere Symbole.",
        "AutoKorrektur: (c) → ©, -> → →, => → ⇒.",
        "Echte Zeichen statt Ersatzkonstruktionen.",
      ],
      task: {
        instruction:
          "Füge ein Copyright-Zeichen (©) und einen Pfeil (→) als echte Zeichen in dein Dokument ein.",
        hint: "Einfügen → Symbol → Weitere Symbole, Subset „Pfeile“. Oder (c) und -> tippen und die AutoKorrektur machen lassen.",
        check: async () => {
          const snapshot = await readDocument();
          const text = snapshot.text;
          const ersatz = (text.match(/->|=>|\(c\)/gi) ?? []).length;
          return fromCriteria(
            [
              criterion("Copyright-Zeichen © gefunden", text.includes("©")),
              criterion("Ein Pfeil (→, ⇒ …) gefunden", PFEILE.test(text)),
              criterion(`Keine Ersatzkonstruktionen wie -> oder (c) (gefunden: ${ersatz})`, ersatz === 0),
            ],
            {
              success: "Echte Zeichen – sie werden richtig gedruckt, gesucht und vorgelesen.",
              failure: "Es fehlt noch ein Zeichen, oder es steht eine Ersatzkonstruktion im Text.",
              hint: "Wenn die AutoKorrektur nicht greift, war sie abgeschaltet – dann über Einfügen → Symbol.",
            },
          );
        },
      },
    },
    {
      id: "unsichtbare-zeichen",
      title: "Zeichen, die man nicht sieht",
      explanation: [
        "Zwei unsichtbare Sonderzeichen verhindern hässliche Zeilenumbrüche. Das geschützte " +
          "Leerzeichen (Strg + Umschalt + Leertaste) hält zusammen, was nicht getrennt " +
          "werden soll: „10 kg“, „Abs. 3“, „Dr. Meier“. Mit eingeblendeten Formatierungs-" +
          "zeichen erscheint es als kleiner Kreis statt als Punkt.",
        "Der bedingte Trennstrich (Strg + Bindestrich) zeigt Word, wo ein langes Wort " +
          "getrennt werden darf – der Strich erscheint nur, wenn das Wort tatsächlich am " +
          "Zeilenende getrennt wird. Ein getippter Bindestrich dagegen bleibt auch dann " +
          "stehen, wenn das Wort später mitten in der Zeile landet.",
      ],
      keyPoints: [
        "Strg + Umschalt + Leertaste = geschütztes Leerzeichen.",
        "Strg + Bindestrich = bedingter Trennstrich.",
        "Nie von Hand trennen – die Trennung bliebe stehen, wenn sich der Text verschiebt.",
      ],
    },
    {
      id: "formel",
      title: "Formeln mit dem Formel-Editor",
      explanation: [
        "„Einfügen“ → „Formel“ (Alt + Umschalt + 0 oder Alt + =) öffnet ein Formelfeld. " +
          "Darin lassen sich Brüche, Wurzeln, Indizes und Summen wie in einem Mathebuch " +
          "setzen. Die Registerkarte „Formel“ bietet fertige Strukturen, und die lineare " +
          "Eingabe versteht Kürzel: a^2+b^2=c^2 und Leertaste ergibt a² + b² = c².",
        "Eine Formel ist ein eigenes Objekt, kein formatierter Text. Sie bleibt beim " +
          "Ändern der Schriftgröße im richtigen Verhältnis, wird von Screenreadern als " +
          "Mathematik vorgelesen und lässt sich in andere Office-Programme übernehmen.",
      ],
      keyPoints: [
        "Alt + = fügt ein Formelfeld ein.",
        "Lineare Eingabe: ^ für Hochstellung, _ für Index, / für Bruch.",
        "Formeln sind Objekte – hochgestellte Ziffern im Fließtext sind kein Ersatz.",
      ],
      task: {
        instruction: "Füge mit dem Formel-Editor den Satz des Pythagoras (a² + b² = c²) als Formel ein.",
        hint: "Alt + = drücken, dann a^2+b^2=c^2 tippen und mit der Leertaste abschließen.",
        check: async () => {
          const formeln = anzahlFormeln(await readBodyOoxml());
          return formeln > 0
            ? fromCriteria([criterion(`${formeln} Formel(n) mit dem Formel-Editor gesetzt`, true)], {
                success: "Eine echte Formel – kein hochgestellter Text.",
                failure: "",
              })
            : fail(
                "Im Dokument steht noch keine Formel aus dem Formel-Editor.",
                "Hochgestellte Ziffern im Fließtext zählen nicht – nutze Einfügen → Formel.",
              );
        },
      },
    },
  ],
};
